import React, { FunctionComponent } from 'react'
import { Link, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { BannerImage } from '../components/banner-image'
import { ContentContainer } from '../components/content-container'
import { HeadBannar } from '../components/head-bannar'
import { PostPaginator } from '../components/post-paginator'
import { SideBar } from '../components/side-bar'
import { useNewsPostsQuery } from '../generated/graphql'
import SvgRightTriangle from '../generated/svg/right-triangle'
import { formatDate } from '../utils/format-date'
import { isInvalidDate, isNotNullish, isNullish } from '../utils/type-check'

export 
const getYearMonth = (str: string | null) => {
  const date = new Date(str ?? NaN)
  if(isInvalidDate(date)) return null
  return getFirstDayOfMonth(date)
}

export const getFirstDayOfMonth = (date: Date) => {
  return new Date(date.getFullYear(), date.getMonth())
}
export const getFirstDayOfNextMonth = (date: Date) => {
  return new Date(date.getFullYear(), date.getMonth() + 1)
}
export const getMonthRange = (date: Date) => {
  return [getFirstDayOfMonth(date).getTime(), getFirstDayOfNextMonth(date).getTime()] as const
}

export const isInRange = (number: number, start: number, end: number) => {
  return start <= number && number < end
}

export const toYearMonth = (date: Date) => {
  if(isInvalidDate(date)) return ''
  return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`
}

export const countByYearMonth = (dates: Date[]) => {
  const map = new Map<number, number>()
  dates.forEach(date => {
    const ym = getFirstDayOfMonth(date).getTime()
    const existingValue = map.get(ym) ?? 0
    map.set(ym, existingValue + 1)
  })
  return map
}

const Outer = styled.div``

export const Inner = styled.div`
  display: flex;
  width: 100%;
  max-width: 60rem;
  box-sizing: border-box;
  @media (max-width: 40rem){
    flex-direction: column;
    padding: 0 2rem;
    gap: 4rem;
  }
`

export const SideBarContainer = styled.div`
  flex: 0 0 12rem;
  @media (max-width: 40rem){
    order: 1;
    flex: 0 0 auto;
  }
`

export const MainContainer = styled.div`
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  min-width: 0;
`

export const PaginatorContainer = styled.div`
  align-self: center;
  margin-top: 2rem;
`

const ItemContainer = styled.div``

export const PostListItem = styled(Link)`
  padding: 2rem;
  border-top: 1px solid #aaa;
  position: relative;
  display: block;
  @media (max-width: 40rem){
    padding: 1rem 0;
  }
  &:last-child {
    border-bottom: 1px solid #aaa;
  }
`

const ItemDate = styled.div`
  font-size: 0.9rem;
  color: #aaa;
  margin-bottom: 1rem;
`

const ItemTitle = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 2rem;
  ${PostListItem}:hover & {
    color: #aaa;
    text-decoration: underline;
  }
`

export const ArrowContainer = styled.div`
  position: absolute;
  right: 0;
  color: #aaa;
  font-size: 0.6rem;
  top: calc(50% - 0.3rem);
  svg {
    display: block;
    height: 0.8rem;
    width: auto;
  }
`

const postInPage = 5

type NewsListPageProps = {
}
export const NewsListPage: FunctionComponent<NewsListPageProps> = () => {
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const targetMonth = getYearMonth(params.get('d'))
  const targetRange = targetMonth ? getMonthRange(targetMonth) : null
  const currentPage = Math.max(0, Number(params.get('p'))) || 1

  const newsPostsQuery = useNewsPostsQuery({variables: {
    first: 100000
  }})

  const filteredPosts = targetRange ? newsPostsQuery.data?.newsPosts?.nodes?.filter(isNotNullish).filter(news => {
    return isInRange(new Date(news?.date ?? NaN).getTime(), ...targetRange)
  }) : newsPostsQuery.data?.newsPosts?.nodes?.filter(isNotNullish)

  const pagedPosts = filteredPosts?.slice(postInPage * (currentPage - 1), postInPage * currentPage)

  return <Outer>
    <HeadBannar desktop={() => <BannerImage src="/images/header_09.jpg" />} mobile={() => <BannerImage src="/images/header_09_mobile.jpg" />}></HeadBannar>
    <ContentContainer>
      <Inner>
        <SideBarContainer>
          <SideBar archiveMap={new Map([
            ...countByYearMonth(newsPostsQuery.data?.newsPosts?.nodes?.map(news => news?.date ? new Date(news.date) : null).filter(isNotNullish) ?? [])
          ])} urlGenerator={(time => `/news?d=${toYearMonth(new Date(time))}`)}/>
        </SideBarContainer>
        <MainContainer>
          <ItemContainer>
            {
              pagedPosts?.map(post => {
                return <PostListItem as="a" key={post.news_postId} href={post.news_fields?.link ?? undefined}>
                  <ItemDate>
                    {formatDate(new Date(post.date ?? NaN))}
                  </ItemDate>
                  <ItemTitle>
                    {post.title}
                  </ItemTitle>
                  <ArrowContainer>
                    <SvgRightTriangle />
                  </ArrowContainer>
                </PostListItem>
              })
            }
          </ItemContainer>
          <PaginatorContainer>
            <PostPaginator
              pageCount={Math.ceil((filteredPosts?.length ?? 0) / postInPage)}
              currentPage={currentPage}
              linkGenerator={page => `/news?${targetMonth ? `d=${toYearMonth(targetMonth)}&` : ''}p=${page}`}
            />
          </PaginatorContainer>
        </MainContainer>
      </Inner>
    </ContentContainer>
  </Outer>
}